import type { Location, NodeEntry, Point } from 'slate';
import { Editor, Path, Range, Text } from 'slate';

import type { DiffSegment } from './layered-diffs';
import { layerDiffs } from './layered-diffs';
import { convertTextToDiffSegment } from './redline-transforms';
import {
  AlineTransforms,
  EditingMode,
  getSuggestionSourceOriginId,
  isParagraphElement,
  Queries,
} from '../internal';
import type {
  AlineChange,
  AlineText,
  ChangeWithoutCount,
  ParagraphElement,
  SuggestionSource,
} from '../internal';

/**
 * Applies a list of AlineChanges to the document at the given location.
 * Changes are applied in reverse order to preserve positional accuracy.
 *
 * @param normalizePoints - When true, normalizes points at offset 0 to the end
 * of the previous node. This is needed for layered diffs where existing edits
 * can cause boundary ambiguity. Should be false for plain text documents.
 * @param sourceByGroupId - Optional map from groupId to SuggestionSource.
 * When provided, uses the source from this map for each change based on the
 * change's groupId. Falls back to the `source` parameter if groupId not found.
 */
function applyAlineChanges(
  editor: Editor,
  options: {
    at: Location;
    authorId: string;
    changes: AlineChange[];
    normalizePoints?: boolean;
    select?: boolean;
    source?: SuggestionSource;
    sourceByGroupId?: Map<string, SuggestionSource>;
  }
): void {
  AlineTransforms.withoutNormalizing(editor, () => {
    const {
      at,
      authorId,
      changes,
      normalizePoints = false,
      select = true,
      source,
      sourceByGroupId,
    } = options;

    // Helper to get source for a specific change
    const getSourceForChange = (change: ChangeWithoutCount): SuggestionSource | undefined => {
      if (sourceByGroupId && change.groupId) {
        return sourceByGroupId.get(change.groupId) ?? source;
      }
      return source;
    };

    const end = Range.end(Editor.range(editor, at));
    const endRef = Editor.pointRef(editor, end);
    const reversedTextEntries = [
      ...Editor.nodes<AlineText>(editor, {
        at,
        match: Text.isText,
        reverse: true,
        voids: true,
      }),
    ];

    // Keep track of the point we're at while suggesting insertions/deletions...
    const state = { point: end };
    for (const change of [...changes].reverse()) {
      if (Queries.getIsBreakChange(change)) {
        switch (change.breakType) {
          case 'add-newline': {
            AlineTransforms.insertBreak(editor, {
              at: state.point,
              authorId,
              editingMode: EditingMode.SUGGESTING,
              select: false,
              source,
            });
            continue;
          }
          case 'delete-newline': {
            const before = Editor.before(editor, state.point);
            if (!before) continue;

            /**
             * Route paragraph-mark deletion through `suggestMergeAtCursor` so
             * the edit lands as a `deleteParagraph` on the previous paragraph's
             * glyphProps. Falls back to the generic deletion path if the merge
             * doesn't apply (e.g. previous block isn't a paragraph).
             */
            const merged = AlineTransforms.suggestMergeAtCursor(editor, {
              authorId,
              cursorPoint: state.point,
              direction: 'backward',
              editingMode: EditingMode.SUGGESTING,
              select: false,
              source,
            });
            if (!merged) {
              AlineTransforms.deleteContent(editor, {
                at: { anchor: state.point, focus: before },
                authorId,
                editingMode: EditingMode.SUGGESTING,
                select: false,
                source,
                voids: true,
              });
            }

            state.point = before;
            continue;
          }
          case 'retain-newline': {
            const before = Editor.before(editor, state.point);
            if (!before) continue;

            state.point = before;
            continue;
          }
        }
      }

      if (change.added) {
        AlineTransforms.insertText(editor, {
          at: state.point,
          authorId,
          editingMode: EditingMode.SUGGESTING,
          select: false,
          source: getSourceForChange(change),
          textToInsert: change.value,
          voids: true,
        });
        // No need to adjust state.point b/c the insertion occurs after state.point.
        continue;
      }

      if (change.removed) {
        const newPoint = getNextPoint(state.point, {
          change,
          textEntries: reversedTextEntries,
        });

        AlineTransforms.deleteContent(editor, {
          at: { anchor: state.point, focus: newPoint },
          authorId,
          editingMode: EditingMode.SUGGESTING,
          select: false,
          source: getSourceForChange(change),
          voids: true,
        });

        // Normalize state.point to prevent boundary ambiguity in next iteration (layered diffs only)
        state.point = normalizePoints
          ? normalizePointToPreviousNode(newPoint, reversedTextEntries)
          : newPoint;
        continue;
      }

      // Noop, but we need to move state.point backwards to ensure we track the right Point.
      const nextPoint = getNextPoint(state.point, { change, textEntries: reversedTextEntries });
      // Normalize to prevent boundary ambiguity when the point lands at offset 0 (layered diffs only)
      state.point = normalizePoints
        ? normalizePointToPreviousNode(nextPoint, reversedTextEntries)
        : nextPoint;
    }

    const endAfterEdits = endRef.unref();
    if (select && endAfterEdits) AlineTransforms.select(editor, endAfterEdits);
  });
}

/**
 * Looks at an `at` Location in the document and takes some `completionText`.
 *
 * We then do a diff between the text at the `at` and the `completionText` and
 * then insert the changes into the document as a diff.
 */
function insertAISuggestions(
  editor: Editor,
  options: {
    completionText: string;
    currentUserId: string;
    at?: Location;
    paragraphSeparator?: string;
    select?: boolean;
    source?: SuggestionSource;
  }
): void {
  const {
    at = editor.selection,
    completionText,
    currentUserId,
    paragraphSeparator = '\n',
    select = true,
    source,
  } = options;
  if (!at) return;

  const selectedText = Queries.string(editor, {
    at,
    paragraphSeparator,
    voids: true,
  });
  const changes = Queries.getAlineChanges(selectedText, completionText);
  const alineChanges = Queries.batchChangesByNewLine(changes);

  applyAlineChanges(editor, {
    at,
    authorId: currentUserId,
    changes: alineChanges,
    select,
    source,
  });
}

/**
 * Calculates the next point position when walking backwards through a document.
 *
 * Given a current point and a change, determines where the point should move
 * after accounting for the change's text. For additions, the point stays put.
 * For deletions or retained text, walks backwards through text nodes to find
 * the start position of the text being processed.
 */
function getNextPoint(
  point: Point,
  options: { change: ChangeWithoutCount; textEntries: NodeEntry<AlineText>[] }
): Point {
  const { change, textEntries } = options;
  if (change.added) return point;

  const entryIndex = textEntries.findIndex(([, textPath]) => Path.equals(point.path, textPath));
  if (entryIndex === -1) return point;

  const tracker: { point: Point; stringLeft: string } = { point, stringLeft: change.value };

  for (let i = entryIndex; i < textEntries.length; i++) {
    const [textNode, textPath] = textEntries[i];
    const isPointInText = Path.equals(textPath, point.path);
    const stringInText = isPointInText ? textNode.text.slice(0, point.offset) : textNode.text;
    const stringExtracted = stringInText.slice(-tracker.stringLeft.length);
    const stringInTextAfterChange = stringInText.slice(0, -tracker.stringLeft.length);

    tracker.point = { offset: stringInTextAfterChange.length, path: textPath };
    tracker.stringLeft = stringExtracted
      ? tracker.stringLeft.slice(0, -stringExtracted.length)
      : tracker.stringLeft;
    if (!tracker.stringLeft) break;
  }

  return tracker.point;
}

/**
 * Normalizes a point at offset 0 to the end of the previous node in document order.
 *
 * When a point is at offset 0 of a text node, it's ambiguous whether it belongs
 * to that node or the previous one. For tracking state.point between operations,
 * we normalize to the end of the previous node to prevent range boundaries from
 * incorrectly including adjacent nodes.
 *
 * Only normalizes to a SIBLING text node (same parent). If the previous text
 * node lives inside a different element — e.g., the empty text leaf inside a
 * `suggestionMarkerStart` / `suggestionMarkerEnd` wrapper — we leave the point
 * alone, otherwise a subsequent `insertText` would treat that empty leaf as
 * the insertion anchor and place the new text *inside* the marker element.
 *
 * This is ONLY used for normalizing state.point, NOT for delete range endpoints.
 */
function normalizePointToPreviousNode(point: Point, textEntries: NodeEntry<AlineText>[]): Point {
  if (point.offset !== 0) return point;

  // Find the current node in textEntries
  const currentIndex = textEntries.findIndex(([, textPath]) => Path.equals(point.path, textPath));
  if (currentIndex === -1) return point;

  // textEntries is reversed, so "previous in document order" is at a higher index
  const prevNodeIndex = currentIndex + 1;
  if (prevNodeIndex >= textEntries.length) return point; // No previous node (start of paragraph)

  const [prevNode, prevPath] = textEntries[prevNodeIndex];

  // Don't cross an element boundary — only normalize to a sibling.
  if (!Path.equals(Path.parent(point.path), Path.parent(prevPath))) return point;

  return { offset: prevNode.text.length, path: prevPath };
}

/**
 * Converts a layered-diffs DiffSegment to an AlineChange (ChangeWithoutCount).
 *
 * This allows us to reuse the existing applyAlineChanges infrastructure
 * after computing layered diffs.
 *
 * For insert/delete changes, preserves the `id` as `groupId` for source tracking.
 */
function layeredChangeToAlineChange(change: DiffSegment): ChangeWithoutCount {
  if (change.op === 'equal') {
    return { added: false, removed: false, value: change.text };
  }
  if (change.op === 'insert') {
    return { added: true, groupId: change.id, removed: false, value: change.text };
  }
  // delete
  return { added: false, groupId: change.id, removed: true, value: change.text };
}

/**
 * Converts an array of layered-diffs DiffSegments to AlineChanges.
 */
export function layeredChangesToAlineChanges(changes: DiffSegment[]): ChangeWithoutCount[] {
  return changes.map(layeredChangeToAlineChange);
}

/**
 * Like insertAISuggestions, but preserves existing diffs/redlines in the document.
 *
 * This function reads the existing diff state from the document (insertNode/deleteNode
 * suggested edits), then uses layerDiffs to compute only the NEW changes needed to
 * reach the target text. This allows AI suggestions to layer properly on top of
 * existing tracked changes.
 *
 * Key behaviors:
 * - Text with existing insertNode that gets deleted → marked with BOTH insert and delete
 * - Text with existing deleteNode that AI wants to keep → new insert is added
 * - Already-deleted text cannot be "double deleted"
 */
function insertAISuggestionsWithLayeredDiffs(
  editor: Editor,
  options: {
    completionText: string;
    currentUserId: string;
    at?: Location;
    paragraphSeparator?: string;
    select?: boolean;
    source?: SuggestionSource;
  }
): void {
  const {
    at = editor.selection,
    completionText,
    currentUserId,
    paragraphSeparator = '\n',
    select = true,
    source,
  } = options;
  if (!at) return;

  /**
   * Step 1: Get all text nodes within the selection (not entire paragraphs).
   * This ensures we only process text that's actually selected, avoiding
   * spurious diffs for content outside the selection.
   */
  const textEntries = [
    ...Editor.nodes<AlineText>(editor, {
      at,
      match: Text.isText,
      voids: true,
    }),
  ];

  if (textEntries.length === 0) return;

  /**
   * Step 2: Convert text nodes to DiffSegment[], inserting paragraph separators when
   * we cross paragraph boundaries. We track the paragraph path to detect boundaries.
   */
  const existingDiffs: DiffSegment[] = [];
  let lastParagraphPath: Path | null = null;

  for (const [textNode, textPath] of textEntries) {
    // Find the containing paragraph path (parent of the text, or grandparent if inside inline)
    const paragraphEntry = Editor.above<ParagraphElement>(editor, {
      at: textPath,
      match: isParagraphElement,
    });
    const currentParagraphPath = paragraphEntry?.[1] ?? null;

    // Insert paragraph separator when crossing to a new paragraph
    if (
      lastParagraphPath &&
      currentParagraphPath &&
      !Path.equals(lastParagraphPath, currentParagraphPath)
    ) {
      existingDiffs.push({ op: 'equal', text: paragraphSeparator });
    }
    lastParagraphPath = currentParagraphPath;

    existingDiffs.push(convertTextToDiffSegment(textNode));
  }

  /**
   * Step 3: Normalize quote styles in completionText to match the document.
   * This prevents quote style differences (curly vs straight) from showing as changes.
   */
  const acceptedText = existingDiffs
    .filter(d => d.op !== 'delete')
    .map(d => d.text)
    .join('');
  const normalizedCompletionText = Queries.normalizeQuoteStyleUsingStyleFrom(
    completionText,
    acceptedText
  );

  /**
   * Step 4: Compute layered diffs.
   * Use the source's generation-event id if available (chat message id or
   * playbook rule job id), otherwise use currentUserId.
   */
  const newId = source ? getSuggestionSourceOriginId(source) : currentUserId;
  const layeredChanges = layerDiffs(existingDiffs, normalizedCompletionText, newId);

  // Step 5: Convert to AlineChange format
  const alineChangesRaw = layeredChangesToAlineChanges(layeredChanges);

  // Step 6: Batch by newline to create BreakChanges
  const alineChanges = Queries.batchChangesByNewLine(alineChangesRaw);

  /**
   * Step 7: Apply using existing function.
   * Enable point normalization for layered diffs to handle boundary ambiguity
   * between existing edit nodes.
   */
  applyAlineChanges(editor, {
    at,
    authorId: currentUserId,
    changes: alineChanges,
    normalizePoints: true,
    select,
    source,
  });
}

const AIAssistantTransforms = {
  applyAlineChanges,
  insertAISuggestions,
  insertAISuggestionsWithLayeredDiffs,
};

export default AIAssistantTransforms;
